import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  CUSTOM_TRACE_UNIT_OPTIONS,
  type AggregationMethod,
  type CustomTraceUnitOptionKey,
} from "../utils/traceConstants"

interface SuggestionOption {
  key: string
  label: string
  description: string
}

/**
 * Max length of the free-text custom unit, kept in sync with the `unit`
 * field's `.max(32)` constraint in the metrics router schema.
 */
export const MAX_CUSTOM_UNIT_LENGTH = 32

type MetricMetadata = MetricRecordData["metric_metadata"]

/**
 * Merges live attribute/span keys observed in recent traces with a curated
 * fallback list. Live keys take precedence and are de-duplicated (trimmed,
 * case-sensitive); fallback options only appear if not already present.
 */
export function mergeSuggestionOptions(
  liveKeys: string[] | undefined,
  fallbackOptions: SuggestionOption[],
): SuggestionOption[] {
  const seen = new Set<string>()
  const options: SuggestionOption[] = []

  for (const key of liveKeys ?? []) {
    const trimmed = key.trim()
    if (!trimmed || seen.has(trimmed)) {
      continue
    }
    seen.add(trimmed)
    options.push({
      key: trimmed,
      label: trimmed,
      description: "Seen in recent traces",
    })
  }

  for (const option of fallbackOptions) {
    if (seen.has(option.key)) {
      continue
    }
    seen.add(option.key)
    options.push(option)
  }

  return options
}

export function getImpliedUnit(
  method: AggregationMethod,
): CustomTraceUnitOptionKey | undefined {
  if (method === "count") {
    return "count"
  }
  if (method === "error_rate" || method === "success_rate") {
    return "percent"
  }
  return undefined
}

export function inferUnitKey(
  metadataUnit: string | undefined,
  hasExplicitUnit: boolean,
): CustomTraceUnitOptionKey {
  if (metadataUnit === "" && hasExplicitUnit) {
    return "custom"
  }
  if (!metadataUnit) {
    return "none"
  }
  const predefined = CUSTOM_TRACE_UNIT_OPTIONS.find(
    (option) => option.key === metadataUnit,
  )
  return predefined ? predefined.key : "custom"
}

export function withoutUnitMetadata(metadata: MetricMetadata): MetricMetadata {
  const {
    unit: _unit,
    units: _units,
    unit_abbreviation: _unitAbbreviation,
    ...metadataWithoutUnit
  } = metadata
  return metadataWithoutUnit
}

export function withoutMetricAttribute(
  metadata: MetricMetadata,
): MetricMetadata {
  const { metric_attribute: _metricAttribute, ...metadataWithoutAttribute } =
    metadata
  return metadataWithoutAttribute
}
